import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdArrowBack,
  MdSave,
  MdAdd,
  MdDelete,
  MdEdit,
  MdDragIndicator,
  MdVisibility,
  MdCancel,
  MdInfo,
  MdFormatBold,
  MdFormatItalic,
  MdFormatUnderlined,
  MdFormatListBulleted,
  MdLink,
  MdTitle,
} from "react-icons/md";
import axiosInstance from "../../api/axiosInstance";

// Icons available for sections
const AVAILABLE_ICONS = [
  { value: "MdInfoOutline", label: "Info / Intro" },
  { value: "MdCollectionsBookmark", label: "Book / Collection" },
  { value: "MdSettingsSuggest", label: "Settings / Gear" },
  { value: "MdCookie", label: "Cookie" },
  { value: "MdLock", label: "Lock / Security" },
  { value: "MdShare", label: "Share / Connections" },
  { value: "MdVerifiedUser", label: "Shield / Trust" },
  { value: "MdChildCare", label: "Child Privacy" },
  { value: "MdUpdateDisabled", label: "Clock / Update" },
  { value: "MdContactMail", label: "Contact Mail" },
];

const PrivacyPolicySettings = () => {
  const navigate = useNavigate();

  // Primary state variables
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState("navigation"); // 'navigation' | 'sections' | 'contact' | 'preview'

  // Form Fields
  const [navigationItems, setNavigationItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [deletedNavIds, setDeletedNavIds] = useState(new Set());
  const [deletedSectionIds, setDeletedSectionIds] = useState(new Set());

  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactAddress, setContactAddress] = useState("");
  const [contactBusinessHours, setContactBusinessHours] = useState("");

  const handleContactChange = (field, value) => {
    const setters = {
      email: setContactEmail,
      phone: setContactPhone,
      address: setContactAddress,
      businessHours: setContactBusinessHours,
    };
    setters[field](value);
    setHasUnsavedChanges(true);
  };

  // Modal / Inline Edit States
  const [editingNavId, setEditingNavId] = useState(null);
  const [newNavTitle, setNewNavTitle] = useState("");

  const [editingSectionId, setEditingSectionId] = useState(null);
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [sectionForm, setSectionForm] = useState({
    id: "",
    sectionNumber: "",
    title: "",
    icon: "MdInfoOutline",
    content: "",
  });

  const editorRef = useRef(null);

  // Fetch data on mount
  useEffect(() => {
    fetchPolicyData();
  }, []);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const fetchPolicyData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/privacy-policy");
      
      if (response.data.success) {
        const data = response.data.data;

        // Items from DB always have real IDs. Static fallback items have no ID — assign temp IDs.
        const now = Date.now();
        const navs = (data.navigationItems || []).map((item, index) => ({
          ...item,
          id: item.id || `temp-nav-${index}-${now + index}`,
        }));
        setNavigationItems(navs);

        const secs = (data.sections || []).map((sec, index) => ({
          ...sec,
          id: sec.id || `temp-sec-${index}-${now + index}`,
        }));
        setSections(secs);

        if (data.contactInfo) {
          setContactEmail(data.contactInfo.email || "");
          setContactPhone(data.contactInfo.phone || "");
          setContactAddress(data.contactInfo.address || "");
          setContactBusinessHours(data.contactInfo.businessHours || "");
        }
      }
      // Reset unsaved changes flag after loading
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error fetching policy data:", err);
      alert("Failed to load Privacy Policy from server. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Drag and Drop (Native HTML5)
  const handleDragStart = (e, index, type) => {
    e.dataTransfer.setData("index", index);
    e.dataTransfer.setData("type", type);
  };

  const handleDrop = (e, targetIndex, type) => {
    const sourceIndex = parseInt(e.dataTransfer.getData("index"));
    const sourceType = e.dataTransfer.getData("type");
    if (sourceType !== type) return;

    if (type === "sections") {
      const items = [...sections];
      const [removed] = items.splice(sourceIndex, 1);
      items.splice(targetIndex, 0, removed);
      setSections(items);
      setHasUnsavedChanges(true);
    } else if (type === "navigation") {
      const items = [...navigationItems];
      const [removed] = items.splice(sourceIndex, 1);
      items.splice(targetIndex, 0, removed);
      setNavigationItems(items);
      setHasUnsavedChanges(true);
    }
  };

  // ─── Navigation Items Management (Local State Only) ────────────────────
  const addNavigationItem = () => {
    if (!newNavTitle.trim()) return;
    const newItem = {
      id: `temp-nav-${Date.now()}`,
      title: newNavTitle,
      sortOrder: navigationItems.length,
    };
    setNavigationItems((prev) => [...prev, newItem]);
    setNewNavTitle("");
    setHasUnsavedChanges(true);
  };


  const updateNavigationItem = (id, updatedTitle) => {
    if (!updatedTitle.trim()) return;
    setNavigationItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, title: updatedTitle } : item))
    );
    setEditingNavId(null);
    setHasUnsavedChanges(true);
  };

  const deleteNavigationItem = (id) => {
    if (!window.confirm("Are you sure you want to delete this navigation item?")) return;
    // Track deletion of real DB items
    if (!id.toString().startsWith("temp-nav-")) {
      setDeletedNavIds((prev) => new Set(prev).add(id));
    }
    setNavigationItems((prev) => prev.filter((item) => item.id !== id));
    setHasUnsavedChanges(true);
  };

  // Section Modal & Editor Helper
  const openSectionModal = (section = null) => {
    if (section) {
      setEditingSectionId(section.id);
      setSectionForm({
        id: section.id,
        sectionNumber: section.sectionNumber || "",
        title: section.title || "",
        icon: section.icon || "MdInfoOutline",
        content: section.content || "",
      });
    } else {
      setEditingSectionId(null);
      setSectionForm({
        id: "",
        sectionNumber: (sections.length + 1).toString(),
        title: "",
        icon: "MdInfoOutline",
        content: "",
      });
    }
    setSectionModalOpen(true);
  };

  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setSectionForm({
        ...sectionForm,
        content: editorRef.current.innerHTML,
      });
    }
  };

  const handleEditorChange = () => {
    if (editorRef.current) {
      setSectionForm({
        ...sectionForm,
        content: editorRef.current.innerHTML,
      });
    }
  };

  // ─── Section CRUD (Local State Only via Modal) ─────────────────────────
  const saveSection = () => {
    if (!sectionForm.title || !sectionForm.content) {
      alert("Title and Content are required");
      return;
    }
    if (editingSectionId) {
      // Update existing section in local state
      setSections((prev) =>
        prev.map((sec) => (sec.id === editingSectionId ? { ...sectionForm } : sec))
      );
    } else {
      // Add new section to local state
      const newSection = {
        ...sectionForm,
        id: `temp-sec-${Date.now()}`,
        sortOrder: sections.length,
      };
      setSections((prev) => [...prev, newSection]);
    }
    setSectionModalOpen(false);
    setHasUnsavedChanges(true);
  };

  const deleteSection = (id) => {
    if (!window.confirm("Are you sure you want to delete this section?")) return;
    // Track deletion of real DB items
    if (!id.toString().startsWith("temp-sec-")) {
      setDeletedSectionIds((prev) => new Set(prev).add(id));
    }
    setSections((prev) => prev.filter((sec) => sec.id !== id));
    setHasUnsavedChanges(true);
  };

  // ─── Save Changes (Complete Synchronization) ───────────
  // All changes are made locally first. This handler syncs everything to backend.
  const handleSaveAll = async () => {
    setSaving(true);
    try {
      // 1. Delete removed navigation items
      for (const id of deletedNavIds) {
        await axiosInstance.delete(`/privacy-policy/navigation/${id}`);
      }

      // 2. Delete removed sections
      for (const id of deletedSectionIds) {
        await axiosInstance.delete(`/privacy-policy/section/${id}`);
      }

      // 3. Sync Navigation Items (create new, update existing)
      for (const item of navigationItems) {
        if (item.id.toString().startsWith("temp-nav-")) {
          // Create new navigation item
          await axiosInstance.post("/privacy-policy/navigation", {
            title: item.title,
            sortOrder: item.sortOrder,
          });
        } else {
          // Update existing navigation item
          await axiosInstance.put(`/privacy-policy/navigation/${item.id}`, {
            title: item.title,
          });
        }
      }

      // 4. Sync Sections (create new, update existing)
      for (const section of sections) {
        if (section.id.toString().startsWith("temp-sec-")) {
          // Create new section
          await axiosInstance.post("/privacy-policy/section", {
            sectionNumber: section.sectionNumber,
            title: section.title,
            icon: section.icon,
            content: section.content,
            sortOrder: section.sortOrder,
          });
        } else {
          // Update existing section
          await axiosInstance.put(`/privacy-policy/section/${section.id}`, {
            sectionNumber: section.sectionNumber,
            title: section.title,
            icon: section.icon,
            content: section.content,
          });
        }
      }

      // 5. Save contact info
      await axiosInstance.put("/privacy-policy", {
        contactInfo: {
          email: contactEmail,
          phone: contactPhone,
          address: contactAddress,
          businessHours: contactBusinessHours,
        },
      });

      // 6. Persist sort order for all items
      await axiosInstance.post("/privacy-policy/reorder", {
        type: "sections",
        items: sections
          .filter((s) => !s.id.toString().startsWith("temp-"))
          .map((sec, index) => ({ id: sec.id, sortOrder: index })),
      });

      await axiosInstance.post("/privacy-policy/reorder", {
        type: "navigation",
        items: navigationItems
          .filter((n) => !n.id.toString().startsWith("temp-"))
          .map((item, index) => ({ id: item.id, sortOrder: index })),
      });

      // 7. Clear deleted tracking sets and reset unsaved flag
      setDeletedNavIds(new Set());
      setDeletedSectionIds(new Set());
      setHasUnsavedChanges(false);

      alert("\u{1F389} Settings saved successfully!");
      // Reload fresh data from database to clear temp IDs
      await fetchPolicyData();
    } catch (err) {
      console.error("Save failed:", err);
      alert(`Failed to save changes: ${err.response?.data?.message || err.message || "Please try again."}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-[#008300] border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-500 font-medium">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="flex items-center gap-2 text-gray-500 hover:text-[#008300] mb-2 transition"
          >
            <MdArrowBack />
            <span>Back to Dashboard</span>
          </button>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-800">Privacy Policy Settings</h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSaveAll}
            disabled={saving || !hasUnsavedChanges}
            className="flex items-center gap-2 bg-[#008300] text-white px-4 sm:px-6 py-3 rounded-xl font-semibold hover:bg-[#006a00] transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            <MdSave className="text-lg sm:text-xl" />
            <span>{saving ? "Saving..." : "Save Changes"}</span>
          </button>
        </div>
      </div>


      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 mb-8 flex flex-wrap gap-1">
        {[
          { id: "navigation", label: "Quick Navigation" },
          { id: "sections", label: "Policy Sections" },
          { id: "contact", label: "Contact Info" },
          { id: "preview", label: "Live Preview" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 rounded-xl font-medium transition text-sm sm:text-base ${
              activeTab === tab.id
                ? "bg-[#008300] text-white shadow-sm"
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Contents */}
      <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-4 sm:p-6 lg:p-8">
        {/* 1. Navigation Items */}
        {activeTab === "navigation" && (
          <div className="space-y-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-850 border-b pb-3 mb-6">Quick Navigation Links</h2>

            {/* Add New */}
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={newNavTitle}
                onChange={(e) => setNewNavTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addNavigationItem(); }}
                placeholder="Add new navigation title..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
              />
              <button
                type="button"
                onClick={addNavigationItem}
                className="flex items-center justify-center gap-2 bg-[#008300] text-white px-4 sm:px-5 rounded-xl font-semibold hover:bg-[#006a00] transition"
              >
                <MdAdd className="text-lg sm:text-xl" />
                <span className="hidden sm:inline">Add</span>
              </button>
            </div>

            {/* List */}
            <div className="space-y-3 mt-6">
              <div className="p-4 bg-blue-50 border border-blue-200 text-blue-800 rounded-xl flex items-start gap-2 text-sm">
                <MdInfo className="text-lg mt-0.5 flex-shrink-0" />
                <p>Drag and drop items using the grab handles to reorder their layout on the policy page.</p>
              </div>

              {navigationItems.map((item, idx) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx, "navigation")}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, idx, "navigation")}
                  className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 border border-gray-200 rounded-xl hover:shadow-sm transition"
                >
                  <div className="flex items-center gap-2 sm:gap-3 flex-1">
                    <MdDragIndicator className="text-gray-400 cursor-grab text-lg sm:text-xl flex-shrink-0" />
                    {editingNavId === item.id ? (
                      <input
                        type="text"
                        defaultValue={item.title}
                        onBlur={(e) => updateNavigationItem(item.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") updateNavigationItem(item.id, e.target.value);
                          if (e.key === "Escape") setEditingNavId(null);
                        }}
                        autoFocus
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg outline-none text-sm sm:text-base"
                      />
                    ) : (
                      <span className="text-gray-700 font-medium text-sm sm:text-base">{item.title}</span>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingNavId(item.id)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      title="Edit Title"
                    >
                      <MdEdit className="text-base sm:text-lg" />
                    </button>
                    <button
                      onClick={() => deleteNavigationItem(item.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Delete Link"
                    >
                      <MdDelete className="text-base sm:text-lg" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3. Sections */}
        {activeTab === "sections" && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 border-b pb-3 mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-855">Policy Content Sections</h2>
              <button
                onClick={() => openSectionModal()}
                className="flex items-center justify-center gap-1.5 bg-[#008300] text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#006a00] transition"
              >
                <MdAdd />
                <span>Create Section</span>
              </button>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 text-blue-800 rounded-xl flex items-start gap-2 text-sm mb-4">
              <MdInfo className="text-lg mt-0.5 flex-shrink-0" />
              <p>Sort order determines the vertical layout of sections on the website. Drag to reorder.</p>
            </div>

            {/* List Sections */}
            <div className="space-y-4">
              {sections.map((section, idx) => (
                <div
                  key={section.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx, "sections")}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, idx, "sections")}
                  className="flex items-center justify-between p-3 sm:p-4 bg-white border border-gray-200 rounded-2xl hover:shadow-md transition gap-3 sm:gap-4"
                >
                  <div className="flex items-center gap-3 sm:gap-4 flex-1 overflow-hidden">
                    <MdDragIndicator className="text-gray-400 cursor-grab text-xl sm:text-2xl flex-shrink-0" />
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 flex-shrink-0 text-sm sm:text-base">
                      {section.sectionNumber || (idx + 1)}
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-bold text-gray-800 truncate text-sm sm:text-base">{section.title}</h4>
                      <p className="text-xs text-[#008300] mt-0.5">{section.icon}</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => openSectionModal(section)}
                      className="p-2 sm:p-2.5 text-blue-600 hover:bg-blue-50 rounded-xl transition"
                      title="Edit"
                    >
                      <MdEdit className="text-lg sm:text-xl" />
                    </button>
                    <button
                      onClick={() => deleteSection(section.id)}
                      className="p-2 sm:p-2.5 text-red-600 hover:bg-red-50 rounded-xl transition"
                      title="Delete"
                    >
                      <MdDelete className="text-lg sm:text-xl" />
                    </button>
                  </div>
                </div>
              ))}

              {sections.length === 0 && (
                <p className="text-center text-gray-500 py-8">No content sections defined. Create one to begin.</p>
              )}
            </div>
          </div>
        )}

        {/* 4. Contact Info */}
        {activeTab === "contact" && (
          <div className="space-y-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-850 border-b pb-3 mb-6">Contact & Support Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Support Email</label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => handleContactChange("email", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Support Phone</label>
                <input
                  type="text"
                  value={contactPhone}
                  onChange={(e) => handleContactChange("phone", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Office Address</label>
              <textarea
                value={contactAddress}
                onChange={(e) => handleContactChange("address", e.target.value)}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none resize-none text-sm sm:text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Hours</label>
              <input
                type="text"
                value={contactBusinessHours}
                onChange={(e) => handleContactChange("businessHours", e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
              />
            </div>
          </div>
        )}

        {/* 3. Live Preview */}
        {activeTab === "preview" && (
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-850 border-b pb-3 mb-6 flex items-center gap-2">
              <MdVisibility className="text-[#008300]" />
              <span>Layout Live Preview</span>
            </h2>
            <div className="border rounded-3xl p-4 sm:p-6 lg:p-10 bg-gray-50 max-h-[500px] sm:max-h-[600px] overflow-y-auto">
              <div className="text-center max-w-xl mx-auto mb-8 sm:mb-10">
                <span className="text-xs font-bold text-[#008300] bg-[#f0faf0] px-3 py-1 rounded-full uppercase tracking-wider">
                  Live View
                </span>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mt-3">Privacy Policy</h1>
                <p className="text-gray-500 mt-2 text-xs sm:text-sm">Preview of policy sections and navigation</p>
              </div>

              {/* Sections list preview */}
              <div className="space-y-4 sm:space-y-6 max-w-3xl mx-auto">
                {sections.map((sec, idx) => (
                  <div key={sec.id || idx} className="bg-white p-4 sm:p-6 rounded-2xl border shadow-sm">
                    <h3 className="text-sm sm:text-base font-bold text-gray-850 border-b pb-2 mb-3 flex items-center gap-2">
                      <span className="w-5 h-5 sm:w-6 sm:h-6 rounded bg-[#008300]/10 text-[#008300] flex items-center justify-center text-xs font-bold">
                        {sec.sectionNumber || (idx + 1)}
                      </span>
                      <span>{sec.title}</span>
                    </h3>
                    <div
                      className="text-xs sm:text-sm text-gray-650 leading-relaxed space-y-2 prose"
                      dangerouslySetInnerHTML={{ __html: sec.content }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section Creation/Edit Modal */}
      {sectionModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-[#008300] text-white">
              <h3 className="text-lg font-bold">
                {editingSectionId ? "Edit Section Content" : "Create New Policy Section"}
              </h3>
              <button
                onClick={() => setSectionModalOpen(false)}
                className="text-white hover:bg-white/10 p-1.5 rounded-full transition"
              >
                <MdCancel className="text-2xl" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4 flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Section Number</label>
                  <input
                    type="text"
                    value={sectionForm.sectionNumber}
                    onChange={(e) => setSectionForm({ ...sectionForm, sectionNumber: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-[#008300]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Section Title</label>
                  <input
                    type="text"
                    value={sectionForm.title}
                    onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-[#008300]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Display Icon</label>
                <select
                  value={sectionForm.icon}
                  onChange={(e) => setSectionForm({ ...sectionForm, icon: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-[#008300]"
                >
                  {AVAILABLE_ICONS.map((ico) => (
                    <option key={ico.value} value={ico.value}>
                      {ico.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Rich Text Editor WYSIWYG */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">Rich Text Content</label>
                <div className="border border-gray-300 rounded-2xl overflow-hidden flex flex-col">
                  {/* Toolbar */}
                  <div className="bg-gray-100 p-2 border-b flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => executeCommand("bold")}
                      className="p-2 hover:bg-gray-250 rounded transition text-gray-700 hover:text-black"
                      title="Bold"
                    >
                      <MdFormatBold className="text-xl" />
                    </button>
                    <button
                      type="button"
                      onClick={() => executeCommand("italic")}
                      className="p-2 hover:bg-gray-250 rounded transition text-gray-700 hover:text-black"
                      title="Italic"
                    >
                      <MdFormatItalic className="text-xl" />
                    </button>
                    <button
                      type="button"
                      onClick={() => executeCommand("underline")}
                      className="p-2 hover:bg-gray-250 rounded transition text-gray-700 hover:text-black"
                      title="Underline"
                    >
                      <MdFormatUnderlined className="text-xl" />
                    </button>
                    <div className="w-[1px] bg-gray-300 mx-1" />
                    <button
                      type="button"
                      onClick={() => executeCommand("insertUnorderedList")}
                      className="p-2 hover:bg-gray-250 rounded transition text-gray-700 hover:text-black"
                      title="Bullet List"
                    >
                      <MdFormatListBulleted className="text-xl" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const url = prompt("Enter link URL:");
                        if (url) executeCommand("createLink", url);
                      }}
                      className="p-2 hover:bg-gray-250 rounded transition text-gray-700 hover:text-black"
                      title="Insert Link"
                    >
                      <MdLink className="text-xl" />
                    </button>
                    <button
                      type="button"
                      onClick={() => executeCommand("formatBlock", "H3")}
                      className="p-2 hover:bg-gray-250 rounded transition text-gray-700 hover:text-black"
                      title="Header 3"
                    >
                      <MdTitle className="text-xl" />
                    </button>
                  </div>
                  {/* Editor Window */}
                  <div
                    ref={editorRef}
                    contentEditable
                    onInput={handleEditorChange}
                    dangerouslySetInnerHTML={{ __html: sectionForm.content }}
                    className="p-4 min-h-[220px] outline-none bg-white overflow-y-auto prose max-w-none text-gray-700"
                    style={{ minHeight: "220px" }}
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
              <button
                onClick={() => setSectionModalOpen(false)}
                className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 font-semibold transition"
              >
                Cancel
              </button>
              <button
                onClick={saveSection}
                className="px-6 py-2.5 rounded-xl bg-[#008300] text-white hover:bg-[#006a00] font-semibold transition"
              >
                {editingSectionId ? "Save Details" : "Create Section"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyPolicySettings;
